/**
 * AgendaCitasPage v2 — Estado del arte estilo Doctolib/NexHealth
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────────┐
 *   │ Header: navegación de fechas + selector vista (D/S/L)   │
 *   ├──────────┬──────────────────────────────────────────────┤
 *   │ Sidebar  │  Main content (cambia por vista)             │
 *   │ - mini   │   - Día: timeline vertical 7am-9pm           │
 *   │   cal    │   - Semana: 7 columnas con timeline          │
 *   │ - KPIs   │   - Lista: agrupada por fecha                │
 *   └──────────┴──────────────────────────────────────────────┘
 *
 * Patrón A: sin check-in. Estados pendiente → confirmada → completada/cancelada/no_show.
 * Click en slot vacío crea cita. Doble-click en cita la edita.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, CircularProgress, Tooltip,
  ToggleButton, ToggleButtonGroup, Skeleton,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  ViewDay as DayViewIcon,
  ViewWeek as WeekViewIcon,
  FormatListBulleted as ListViewIcon,
  WhatsApp as WhatsAppIcon,
  DirectionsWalk as WalkIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Language as PortalIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  HourglassEmpty as PendingIcon,
  EventBusy as NoShowIcon,
} from '@mui/icons-material';
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import DialogNuevaCita from './DialogNuevaCita';
import DialogBuscarHueco from './DialogBuscarHueco';

const API_URL = '/api';

const DIAS_LARGO = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const DIAS_CORTO = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Configuración del timeline
const HORA_INICIO = 7;   // 7am
const HORA_FIN = 21;     // 9pm
const HORA_TRABAJO_INICIO = 8;
const HORA_TRABAJO_FIN = 19;
const SLOT_MINUTOS = 30;
const PIXELES_POR_HORA = 64;

const ESTADO_CONFIG = {
  null:        { label: 'Por confirmar', color: '#92400e', bg: '#fef3c7', border: '#f59e0b', icon: <PendingIcon sx={{ fontSize: 12 }} /> },
  '':          { label: 'Por confirmar', color: '#92400e', bg: '#fef3c7', border: '#f59e0b', icon: <PendingIcon sx={{ fontSize: 12 }} /> },
  confirmado:  { label: 'Confirmada',    color: '#15803d', bg: '#dcfce7', border: '#22c55e', icon: <CheckIcon sx={{ fontSize: 12 }} /> },
  completada:  { label: 'Completada',    color: '#1e40af', bg: '#dbeafe', border: '#3b82f6', icon: <CheckIcon sx={{ fontSize: 12 }} /> },
  cancelado:   { label: 'Cancelada',     color: '#64748b', bg: '#f1f5f9', border: '#94a3b8', icon: <CancelIcon sx={{ fontSize: 12 }} /> },
  no_show:     { label: 'No asistió',    color: '#991b1b', bg: '#fee2e2', border: '#dc2626', icon: <NoShowIcon sx={{ fontSize: 12 }} /> },
};

const TIPO_CONFIG = {
  primera_vez:    { label: 'Primera', accent: '#1e3a5f' },
  subsecuente:    { label: 'Control', accent: '#0ea5e9' },
  urgencia:       { label: 'Urgencia', accent: '#dc2626' },
  interconsulta:  { label: 'Interconsulta', accent: '#7c3aed' },
  valoracion:     { label: 'Valoración', accent: '#15803d' },
};

const ORIGEN_CONFIG = {
  chatbot:  { label: 'Chatbot',  icon: <WhatsAppIcon /> },
  manual:   { label: 'Manual',   icon: <EditIcon /> },
  walk_in:  { label: 'Walk-in',  icon: <WalkIcon /> },
  telefono: { label: 'Teléfono', icon: <PhoneIcon /> },
  portal:   { label: 'Portal',   icon: <PortalIcon /> },
};

function getEstadoCfg(estado) {
  return ESTADO_CONFIG[estado || ''] || ESTADO_CONFIG.null;
}

function fmtHora(d) {
  return new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function inicioSemana(fecha) {
  const d = new Date(fecha);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function mismoDia(a, b) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

// ============================================================================
export default function AgendaCitasPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [vista, setVista] = useState(() => localStorage.getItem('agenda_vista') || 'dia');
  const [fechaActual, setFechaActual] = useState(new Date());
  const [citas, setCitas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogNueva, setDialogNueva] = useState({ open: false, fechaSugerida: null });
  const [dialogEditar, setDialogEditar] = useState({ open: false, cita: null });
  const [dialogBuscarHueco, setDialogBuscarHueco] = useState(false);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (u) {
        const stored = localStorage.getItem('user_id');
        if (stored) setUserId(stored);
        else {
          try {
            const r = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const d = await r.json();
            if (d?.id) {
              localStorage.setItem('user_id', d.id);
              setUserId(d.id);
            }
          } catch (e) { console.error(e); }
        }
      } else navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Persistir vista
  useEffect(() => { localStorage.setItem('agenda_vista', vista); }, [vista]);

  // Calcular rango de fechas según vista
  const rangoFechas = useMemo(() => {
    if (vista === 'dia') {
      const ini = new Date(fechaActual); ini.setHours(0, 0, 0, 0);
      const fin = new Date(ini); fin.setDate(fin.getDate() + 1);
      return { inicio: ini, fin };
    }
    if (vista === 'semana') {
      const ini = inicioSemana(fechaActual);
      const fin = new Date(ini); fin.setDate(fin.getDate() + 7);
      return { inicio: ini, fin };
    }
    // lista: rango más amplio (mes completo)
    const ini = new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1);
    const fin = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 1);
    return { inicio: ini, fin };
  }, [vista, fechaActual]);

  const cargarCitas = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        fecha_inicio: rangoFechas.inicio.toISOString(),
        fecha_fin: rangoFechas.fin.toISOString(),
        medico_id: userId,
        limit: '200',
      });
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/emr/citas?${params}`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/emr/citas/estadisticas?medico_id=${userId}`, { headers: { 'X-User-Id': userId } }),
      ]);
      if (r1.ok) setCitas((await r1.json()).citas || []);
      if (r2.ok) setEstadisticas(await r2.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [userId, rangoFechas]);

  useEffect(() => { if (userId) cargarCitas(); }, [userId, cargarCitas]);

  // Auto-refresh cada 60s para captar citas del chatbot
  useEffect(() => {
    if (!userId) return;
    const t = setInterval(cargarCitas, 60000);
    return () => clearInterval(t);
  }, [userId, cargarCitas]);

  // Estado de drag activo (mostrar feedback visual a nivel global)
  const dragRef = useRef({ citaId: null, offsetY: 0 });
  const [dragActivo, setDragActivo] = useState(false);

  const handleCitaDragStart = (cita, offsetY) => {
    dragRef.current = { citaId: cita.id, offsetY };
    setDragActivo(true);
  };

  const handleCitaDragEnd = () => {
    dragRef.current = { citaId: null, offsetY: 0 };
    setDragActivo(false);
  };

  // Mueve una cita a una nueva fecha/hora preservando duración
  const handleCitaMove = async (citaId, nuevaFecha) => {
    setDragActivo(false);
    const citaOriginal = citas.find(c => c.id === citaId);
    // Optimistic update
    setCitas((prev) => prev.map(c =>
      c.id === citaId ? { ...c, fecha_cita: nuevaFecha.toISOString() } : c
    ));
    try {
      const res = await fetch(`${API_URL}/emr/citas/${citaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ fecha_cita: nuevaFecha.toISOString() }),
      });
      if (res.status === 409) {
        // Conflicto detectado — preguntar al usuario
        const data = await res.json();
        const detalle = data.detail || {};
        const lista = (detalle.conflictos || []).map(c => {
          const f = new Date(c.fecha_cita);
          return `• ${c.paciente_nombre} (${f.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })})`;
        }).join('\n');
        const ok = window.confirm(
          `⚠ Conflicto de horario\n\n${lista}\n\nBuffer: ${detalle.buffer_min} min entre citas.\n\n¿Mover de todos modos?`
        );
        if (ok) {
          const res2 = await fetch(`${API_URL}/emr/citas/${citaId}?forzar=true`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({ fecha_cita: nuevaFecha.toISOString() }),
          });
          if (!res2.ok) cargarCitas();
        } else {
          // Cancelar: revertir
          if (citaOriginal) {
            setCitas((prev) => prev.map(c => c.id === citaId ? citaOriginal : c));
          } else {
            cargarCitas();
          }
        }
        return;
      }
      if (!res.ok) {
        cargarCitas();
        return;
      }
    } catch (e) {
      console.error('Error moviendo cita:', e);
      cargarCitas();
    }
  };

  // Cambia duración de cita (resize)
  const handleCitaResize = async (citaId, nuevaDuracion) => {
    const citaOriginal = citas.find(c => c.id === citaId);
    setCitas((prev) => prev.map(c =>
      c.id === citaId ? { ...c, duracion_minutos: nuevaDuracion } : c
    ));
    try {
      const res = await fetch(`${API_URL}/emr/citas/${citaId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ duracion_minutos: nuevaDuracion }),
      });
      if (res.status === 409) {
        const data = await res.json();
        const detalle = data.detail || {};
        const lista = (detalle.conflictos || []).map(c => `• ${c.paciente_nombre}`).join('\n');
        const ok = window.confirm(`⚠ La nueva duración chocaría con:\n\n${lista}\n\n¿Cambiar duración igualmente?`);
        if (ok) {
          await fetch(`${API_URL}/emr/citas/${citaId}?forzar=true`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
            body: JSON.stringify({ duracion_minutos: nuevaDuracion }),
          });
        } else if (citaOriginal) {
          setCitas((prev) => prev.map(c => c.id === citaId ? citaOriginal : c));
        }
        return;
      }
      if (!res.ok) cargarCitas();
    } catch (e) {
      console.error('Error cambiando duración:', e);
      cargarCitas();
    }
  };

  // Navegación
  const navAnt = () => {
    const d = new Date(fechaActual);
    if (vista === 'dia') d.setDate(d.getDate() - 1);
    else if (vista === 'semana') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setFechaActual(d);
  };
  const navSig = () => {
    const d = new Date(fechaActual);
    if (vista === 'dia') d.setDate(d.getDate() + 1);
    else if (vista === 'semana') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setFechaActual(d);
  };
  const navHoy = () => setFechaActual(new Date());

  const abrirNueva = (fechaSugerida = null) => {
    setDialogNueva({ open: true, fechaSugerida: fechaSugerida || fechaActual });
  };

  const abrirEditar = (cita) => {
    setDialogEditar({ open: true, cita });
  };

  const tituloVista = () => {
    if (vista === 'dia') {
      return `${DIAS_LARGO[fechaActual.getDay()]}, ${fechaActual.getDate()} de ${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
    }
    if (vista === 'semana') {
      const ini = inicioSemana(fechaActual);
      const fin = new Date(ini); fin.setDate(fin.getDate() + 6);
      return `${ini.getDate()} ${MESES[ini.getMonth()].slice(0,3)} - ${fin.getDate()} ${MESES[fin.getMonth()].slice(0,3)} ${fin.getFullYear()}`;
    }
    return `${MESES[fechaActual.getMonth()]} ${fechaActual.getFullYear()}`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {/* Hoy + flechas */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button
              size="small" startIcon={<TodayIcon />} onClick={navHoy}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12.5,
                color: '#1e3a5f', borderRadius: 2, px: 1.5,
                border: '1px solid #e2e8f0',
                '&:hover': { background: '#eff6ff', borderColor: '#1e3a5f' },
              }}
            >
              Hoy
            </Button>
            <IconButton size="small" onClick={navAnt} sx={{ color: '#475569' }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton size="small" onClick={navSig} sx={{ color: '#475569' }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Título */}
          <Typography sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 700, color: '#1e293b', flex: 1 }}>
            {tituloVista()}
          </Typography>

          {/* Selector vista */}
          <ToggleButtonGroup
            value={vista} exclusive size="small"
            onChange={(_, v) => v && setVista(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: 12, fontWeight: 700,
                px: 1.5, py: 0.6, gap: 0.5, color: '#475569',
                border: '1px solid #e2e8f0',
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                  color: '#fff', borderColor: 'transparent',
                  '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
                },
              },
            }}
          >
            <ToggleButton value="dia"><DayViewIcon sx={{ fontSize: 16 }} /> Día</ToggleButton>
            <ToggleButton value="semana"><WeekViewIcon sx={{ fontSize: 16 }} /> Semana</ToggleButton>
            <ToggleButton value="lista"><ListViewIcon sx={{ fontSize: 16 }} /> Lista</ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Recargar">
            <IconButton size="small" onClick={cargarCitas} sx={{ color: '#475569' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            size="small" startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
            onClick={() => setDialogBuscarHueco(true)}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12.5,
              color: '#1e3a5f', borderRadius: 2, px: 1.5,
              border: '1px solid #e2e8f0',
              '&:hover': { background: '#eff6ff', borderColor: '#1e3a5f' },
            }}
          >
            Buscar hueco
          </Button>

          <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => abrirNueva()}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12.5, borderRadius: 2, px: 2,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              boxShadow: '0 4px 12px rgba(30,58,95,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
            }}
          >
            Nueva cita
          </Button>
        </Box>
      </Box>

      {/* Layout principal: sidebar + content */}
      <Box sx={{ display: 'flex', gap: 2, p: { xs: 2, md: 2 } }}>

        {/* SIDEBAR — mini-cal + KPIs (oculto en móvil) */}
        <Box sx={{ width: 260, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <MiniCalendar
            fechaActual={fechaActual}
            onSelect={(d) => { setFechaActual(d); setVista('dia'); }}
            citas={citas}
          />
          <KpiSidebar estadisticas={estadisticas} />
        </Box>

        {/* CONTENT */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Skeleton variant="rounded" height={600} sx={{ borderRadius: 3 }} />
          ) : vista === 'dia' ? (
            <VistaDia
              fecha={fechaActual} citas={citas}
              onSlotClick={(fecha) => abrirNueva(fecha)}
              onCitaClick={abrirEditar}
              onCitaMove={handleCitaMove}
              onCitaResize={handleCitaResize}
              onDragStart={handleCitaDragStart}
              onDragEnd={handleCitaDragEnd}
              dragActivo={dragActivo}
              dragRef={dragRef}
            />
          ) : vista === 'semana' ? (
            <VistaSemana
              fechaActual={fechaActual} citas={citas}
              onSlotClick={(fecha) => abrirNueva(fecha)}
              onCitaClick={abrirEditar}
              onDiaClick={(d) => { setFechaActual(d); setVista('dia'); }}
              onCitaMove={handleCitaMove}
              onCitaResize={handleCitaResize}
              onDragStart={handleCitaDragStart}
              onDragEnd={handleCitaDragEnd}
              dragActivo={dragActivo}
              dragRef={dragRef}
            />
          ) : (
            <VistaLista
              fechaActual={fechaActual} citas={citas}
              onCitaClick={abrirEditar}
            />
          )}
        </Box>
      </Box>

      {/* Dialog nueva cita */}
      <DialogNuevaCita
        open={dialogNueva.open}
        onClose={() => setDialogNueva({ open: false, fechaSugerida: null })}
        userId={userId}
        fechaSugerida={dialogNueva.fechaSugerida}
        onGuardado={() => {
          setDialogNueva({ open: false, fechaSugerida: null });
          cargarCitas();
        }}
      />

      {/* Dialog editar cita */}
      <DialogNuevaCita
        open={dialogEditar.open}
        onClose={() => setDialogEditar({ open: false, cita: null })}
        userId={userId}
        cita={dialogEditar.cita}
        onGuardado={() => {
          setDialogEditar({ open: false, cita: null });
          cargarCitas();
        }}
        onEliminado={() => {
          setDialogEditar({ open: false, cita: null });
          cargarCitas();
        }}
        onIniciarConsulta={(cita) => {
          setDialogEditar({ open: false, cita: null });
          const params = new URLSearchParams({
            cita_id: String(cita.id),
            paciente_telefono: cita.numero_whatsapp || '',
            paciente_nombre: cita.paciente_nombre || '',
          });
          navigate(`/emr?${params.toString()}`);
        }}
      />

      {/* Dialog buscar hueco */}
      <DialogBuscarHueco
        open={dialogBuscarHueco}
        onClose={() => setDialogBuscarHueco(false)}
        userId={userId}
        onSlotElegido={(fecha) => {
          setDialogBuscarHueco(false);
          abrirNueva(fecha);
        }}
      />
    </Box>
  );
}

// ============================================================================
// VISTA DÍA — timeline vertical
// ============================================================================
function VistaDia({ fecha, citas, onSlotClick, onCitaClick, onCitaMove, onCitaResize, onDragStart, onDragEnd, dragActivo, dragRef }) {
  const esHoy = mismoDia(fecha, new Date());
  const citasDia = citas
    .filter(c => mismoDia(c.fecha_cita, fecha))
    .sort((a, b) => new Date(a.fecha_cita) - new Date(b.fecha_cita));

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <TimelineColumna
        fecha={fecha} citas={citasDia} esHoy={esHoy}
        ancho="100%" mostrarHoras={true}
        onSlotClick={onSlotClick} onCitaClick={onCitaClick}
        onCitaMove={onCitaMove} onCitaResize={onCitaResize}
        onDragStart={onDragStart} onDragEnd={onDragEnd}
        dragActivo={dragActivo} dragRef={dragRef}
      />
    </Paper>
  );
}

// ============================================================================
// VISTA SEMANA — 7 columnas
// ============================================================================
function VistaSemana({ fechaActual, citas, onSlotClick, onCitaClick, onDiaClick, onCitaMove, onCitaResize, onDragStart, onDragEnd, dragActivo, dragRef }) {
  const ini = inicioSemana(fechaActual);
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(ini); d.setDate(d.getDate() + i); return d;
  });
  const hoy = new Date();

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      {/* Header de días */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <Box sx={{ width: 56, borderRight: '1px solid #e2e8f0' }} />
        {dias.map((d, i) => {
          const esHoy = mismoDia(d, hoy);
          const cuenta = citas.filter(c => mismoDia(c.fecha_cita, d)).length;
          return (
            <Box key={i}
              onClick={() => onDiaClick(d)}
              sx={{
                flex: 1, minWidth: 0, py: 1.2, textAlign: 'center', cursor: 'pointer',
                borderRight: i < 6 ? '1px solid #e2e8f0' : 'none',
                background: esHoy ? '#eff6ff' : 'transparent',
                '&:hover': { background: '#f1f5f9' },
              }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {DIAS_CORTO[d.getDay()]}
              </Typography>
              <Typography sx={{
                fontSize: 18, fontWeight: 800,
                color: esHoy ? '#1e3a5f' : '#1e293b',
                mt: 0.2,
              }}>
                {d.getDate()}
                {esHoy && (
                  <Box component="span" sx={{
                    ml: 0.6, fontSize: 9, fontWeight: 800, color: '#fff',
                    background: '#1e3a5f', px: 0.6, py: 0.1, borderRadius: 1, verticalAlign: 'middle',
                  }}>HOY</Box>
                )}
              </Typography>
              {cuenta > 0 && (
                <Typography sx={{ fontSize: 10, color: '#64748b', mt: 0.1 }}>
                  {cuenta} cita{cuenta !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Timelines paralelos */}
      <Box sx={{ display: 'flex', overflowX: 'auto' }}>
        {/* Rail de horas */}
        <Box sx={{ width: 56, flexShrink: 0, borderRight: '1px solid #e2e8f0', position: 'sticky', left: 0, background: '#fff', zIndex: 2 }}>
          <RailHoras />
        </Box>
        {dias.map((d, i) => {
          const citasDia = citas.filter(c => mismoDia(c.fecha_cita, d));
          return (
            <Box key={i} sx={{ flex: 1, minWidth: 110, borderRight: i < 6 ? '1px solid #e2e8f0' : 'none' }}>
              <TimelineColumna
                fecha={d} citas={citasDia} esHoy={mismoDia(d, hoy)}
                mostrarHoras={false} compacta={true}
                onSlotClick={onSlotClick} onCitaClick={onCitaClick}
                onCitaMove={onCitaMove} onCitaResize={onCitaResize}
                onDragStart={onDragStart} onDragEnd={onDragEnd}
                dragActivo={dragActivo} dragRef={dragRef}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ============================================================================
// TIMELINE COLUMNA — base reutilizada por Día y Semana
// ============================================================================
function TimelineColumna({ fecha, citas, esHoy, mostrarHoras, compacta, onSlotClick, onCitaClick, onCitaMove, onCitaResize, onDragStart, onDragEnd, dragActivo, dragRef }) {
  const [ahoraTop, setAhoraTop] = useState(null);
  const [dragOverY, setDragOverY] = useState(null);
  const colRef = useRef(null);
  const horas = useMemo(() => {
    const arr = [];
    for (let h = HORA_INICIO; h <= HORA_FIN; h++) arr.push(h);
    return arr;
  }, []);

  // Convierte un Y dentro de la columna a una nueva fecha (snap a 15 min)
  const yToFecha = (y) => {
    const minutosTotales = Math.round((y / PIXELES_POR_HORA) * 60 / 15) * 15;
    const horas = Math.floor(minutosTotales / 60);
    const minutos = minutosTotales % 60;
    const newDate = new Date(fecha);
    newDate.setHours(HORA_INICIO + horas, minutos, 0, 0);
    return newDate;
  };

  const handleDragOver = (e) => {
    if (!dragRef?.current?.citaId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (colRef.current) {
      const rect = colRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top - (dragRef.current.offsetY || 0);
      // Snap visual a 15 min
      const snapped = Math.round(y / 16) * 16;
      setDragOverY(snapped);
    }
  };

  const handleDragLeave = (e) => {
    // Solo limpiar si realmente salimos de la columna
    if (e.currentTarget === e.target) setDragOverY(null);
  };

  const handleDrop = (e) => {
    if (!dragRef?.current?.citaId) return;
    e.preventDefault();
    if (colRef.current) {
      const rect = colRef.current.getBoundingClientRect();
      const y = e.clientY - rect.top - (dragRef.current.offsetY || 0);
      const nueva = yToFecha(Math.max(0, y));
      onCitaMove?.(dragRef.current.citaId, nueva);
    }
    setDragOverY(null);
  };

  // Indicador "ahora" (solo si es hoy)
  useEffect(() => {
    if (!esHoy) { setAhoraTop(null); return; }
    const update = () => {
      const now = new Date();
      const hora = now.getHours() + now.getMinutes() / 60;
      if (hora < HORA_INICIO || hora > HORA_FIN) { setAhoraTop(null); return; }
      setAhoraTop((hora - HORA_INICIO) * PIXELES_POR_HORA);
    };
    update();
    const t = setInterval(update, 60000);
    return () => clearInterval(t);
  }, [esHoy]);

  const handleSlotClick = (h, m) => {
    const f = new Date(fecha);
    f.setHours(h, m, 0, 0);
    onSlotClick?.(f);
  };

  const altoTotal = (HORA_FIN - HORA_INICIO + 1) * PIXELES_POR_HORA;

  return (
    <Box sx={{ display: 'flex', position: 'relative' }}>
      {mostrarHoras && (
        <Box sx={{ width: 56, flexShrink: 0, borderRight: '1px solid #e2e8f0' }}>
          <RailHoras />
        </Box>
      )}

      {/* Columna principal */}
      <Box
        ref={colRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          flex: 1, position: 'relative', height: altoTotal,
          background: dragActivo ? '#fafbff' : '#fff',
          transition: 'background 0.15s',
        }}
      >
        {/* Indicador de drop position */}
        {dragOverY !== null && (
          <Box sx={{
            position: 'absolute', left: 4, right: 4,
            top: dragOverY, height: 2,
            background: '#1e3a5f', zIndex: 5, borderRadius: 1,
            pointerEvents: 'none',
            '&::before': {
              content: '""', position: 'absolute', left: -3, top: -3,
              width: 8, height: 8, borderRadius: '50%', background: '#1e3a5f',
            },
          }} />
        )}

        {/* Slots de fondo (clic para crear) */}
        {horas.map((h) => (
          <React.Fragment key={h}>
            <Box
              onClick={() => handleSlotClick(h, 0)}
              sx={{
                position: 'absolute', left: 0, right: 0,
                top: (h - HORA_INICIO) * PIXELES_POR_HORA,
                height: PIXELES_POR_HORA / 2,
                background: (h < HORA_TRABAJO_INICIO || h >= HORA_TRABAJO_FIN) ? '#f8fafc' : 'transparent',
                borderTop: '1px solid #e2e8f0',
                cursor: 'pointer', zIndex: 0,
                '&:hover': { background: 'rgba(30,58,95,0.04)', '& .new-hint': { opacity: 1 } },
              }}>
              <Box className="new-hint" sx={{
                opacity: 0, fontSize: 10, color: '#1e3a5f', fontWeight: 700,
                p: 0.3, transition: 'opacity 0.15s',
              }}>+ Nueva</Box>
            </Box>
            <Box
              onClick={() => handleSlotClick(h, 30)}
              sx={{
                position: 'absolute', left: 0, right: 0,
                top: (h - HORA_INICIO) * PIXELES_POR_HORA + PIXELES_POR_HORA / 2,
                height: PIXELES_POR_HORA / 2,
                background: (h < HORA_TRABAJO_INICIO || h >= HORA_TRABAJO_FIN) ? '#f8fafc' : 'transparent',
                borderTop: '1px dashed #f1f5f9',
                cursor: 'pointer', zIndex: 0,
                '&:hover': { background: 'rgba(30,58,95,0.04)' },
              }} />
          </React.Fragment>
        ))}

        {/* Indicador "ahora" */}
        {ahoraTop !== null && (
          <Box sx={{
            position: 'absolute', left: -4, right: 0,
            top: ahoraTop, height: 2,
            background: '#dc2626', zIndex: 4,
            '&::before': {
              content: '""', position: 'absolute', left: -6, top: -4,
              width: 10, height: 10, borderRadius: '50%', background: '#dc2626',
              boxShadow: '0 0 0 3px rgba(220,38,38,0.2)',
            },
          }} />
        )}

        {/* Citas posicionadas */}
        {citas.map((c) => (
          <CitaCard
            key={c.id} cita={c} compacta={compacta}
            onClick={() => onCitaClick(c)}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onResize={onCitaResize}
          />
        ))}
      </Box>
    </Box>
  );
}

function RailHoras() {
  const horas = useMemo(() => {
    const arr = [];
    for (let h = HORA_INICIO; h <= HORA_FIN; h++) arr.push(h);
    return arr;
  }, []);
  return (
    <Box sx={{ height: (HORA_FIN - HORA_INICIO + 1) * PIXELES_POR_HORA, position: 'relative' }}>
      {horas.map(h => {
        const ampm = h < 12 ? 'am' : 'pm';
        const hh = h <= 12 ? h : h - 12;
        return (
          <Box key={h} sx={{
            position: 'absolute', left: 0, right: 0,
            top: (h - HORA_INICIO) * PIXELES_POR_HORA - 7,
            textAlign: 'center',
          }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.3 }}>
              {hh} {ampm}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ============================================================================
// CITA CARD — tarjeta posicionada en el timeline
// ============================================================================
function CitaCard({ cita, compacta, onClick, onDragStart, onDragEnd, onResize }) {
  const fecha = new Date(cita.fecha_cita);
  const horas = fecha.getHours() + fecha.getMinutes() / 60;
  const top = (horas - HORA_INICIO) * PIXELES_POR_HORA;
  const dur = cita.duracion_minutos || 30;
  const altura = Math.max(20, (dur / 60) * PIXELES_POR_HORA);
  const [arrastrando, setArrastrando] = useState(false);
  const [resizing, setResizing] = useState(false);

  const cfg = getEstadoCfg(cita.estado);
  const tipoCfg = TIPO_CONFIG[cita.tipo_consulta] || TIPO_CONFIG.primera_vez;
  const origenCfg = ORIGEN_CONFIG[cita.origen] || ORIGEN_CONFIG.manual;

  // Si está cancelada, opacidad reducida + no draggable
  const cancelada = cita.estado === 'cancelado';

  const handleDragStart = (e) => {
    if (cancelada) { e.preventDefault(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    e.dataTransfer.effectAllowed = 'move';
    // Imagen fantasma sutil (default funciona pero podemos custom)
    try { e.dataTransfer.setData('text/plain', String(cita.id)); } catch (_) {}
    setArrastrando(true);
    onDragStart?.(cita, offsetY);
  };

  const handleDragEndCard = () => {
    setArrastrando(false);
    onDragEnd?.();
  };

  // Resize: drag manual del borde inferior
  const handleResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setResizing(true);
    const startY = e.clientY;
    const startDur = dur;

    const onMove = (ev) => {
      const delta = ev.clientY - startY;
      const minDelta = (delta / PIXELES_POR_HORA) * 60;
      // Snap a 15 min, mínimo 15
      const nuevaDur = Math.max(15, Math.round((startDur + minDelta) / 15) * 15);
      // Update visual provisional vía DOM
      // (no actualizamos state aquí — solo al soltar)
      e.target.dataset.previewDur = nuevaDur;
      const card = e.target.parentElement;
      if (card) {
        card.style.height = `${Math.max(20, (nuevaDur / 60) * PIXELES_POR_HORA - 2)}px`;
      }
    };

    const onUp = (ev) => {
      const delta = ev.clientY - startY;
      const minDelta = (delta / PIXELES_POR_HORA) * 60;
      const nuevaDur = Math.max(15, Math.round((startDur + minDelta) / 15) * 15);
      setResizing(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (nuevaDur !== startDur) onResize?.(cita.id, nuevaDur);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <Tooltip
      arrow placement="right"
      disableHoverListener={arrastrando || resizing}
      title={
        <Box sx={{ p: 0.4 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.2 }}>{cita.paciente_nombre}</Typography>
          <Typography sx={{ fontSize: 10, opacity: 0.85 }}>
            {fmtHora(cita.fecha_cita)} · {dur} min · {tipoCfg.label}
          </Typography>
          {cita.motivo && <Typography sx={{ fontSize: 10, opacity: 0.85, mt: 0.3 }}>📝 {cita.motivo}</Typography>}
          <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.3 }}>{origenCfg.label}</Typography>
        </Box>
      }
    >
      <Box
        draggable={!cancelada}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEndCard}
        onClick={(e) => { e.stopPropagation(); if (!resizing) onClick?.(cita); }}
        sx={{
          position: 'absolute', left: 4, right: 4,
          top, height: altura - 2,
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          borderLeft: `4px solid ${tipoCfg.accent}`,
          borderRadius: 1.5,
          padding: compacta ? 0.4 : 0.6,
          cursor: cancelada ? 'pointer' : 'grab',
          overflow: 'hidden',
          opacity: arrastrando ? 0.4 : (cancelada ? 0.55 : 1),
          textDecoration: cancelada ? 'line-through' : 'none',
          transition: arrastrando ? 'none' : 'all 0.15s',
          zIndex: arrastrando ? 10 : 2,
          '&:hover': {
            transform: arrastrando ? 'none' : 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(15,23,42,0.12)',
            zIndex: 3,
            '& .resize-handle': { opacity: 1 },
          },
          '&:active': { cursor: cancelada ? 'pointer' : 'grabbing' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.1 }}>
          {React.cloneElement(origenCfg.icon, { sx: { fontSize: 11, color: cfg.color, opacity: 0.8 } })}
          <Typography sx={{
            fontSize: compacta ? 10 : 11.5, fontWeight: 700, color: cfg.color,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {fmtHora(cita.fecha_cita)} {cita.paciente_nombre}
          </Typography>
        </Box>
        {altura >= 36 && !compacta && (
          <Typography sx={{
            fontSize: 10.5, color: cfg.color, opacity: 0.85,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {cita.motivo || tipoCfg.label}
          </Typography>
        )}
        {altura >= 50 && !compacta && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.3 }}>
            {cfg.icon && React.cloneElement(cfg.icon, { sx: { fontSize: 10, color: cfg.color } })}
            <Typography sx={{ fontSize: 9.5, color: cfg.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              {cfg.label}
            </Typography>
          </Box>
        )}

        {/* Resize handle (borde inferior) */}
        {!cancelada && (
          <Box
            className="resize-handle"
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              height: 6, cursor: 'ns-resize',
              opacity: 0, transition: 'opacity 0.15s',
              background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.08))',
              zIndex: 4,
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
              '&::before': {
                content: '""',
                width: 24, height: 2, marginBottom: 1,
                background: cfg.color, borderRadius: 1, opacity: 0.6,
              },
              '&:hover': {
                opacity: 1,
                background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.15))',
              },
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

// ============================================================================
// VISTA LISTA — agrupada por día
// ============================================================================
function VistaLista({ fechaActual, citas, onCitaClick }) {
  const grupos = useMemo(() => {
    const map = new Map();
    for (const c of citas) {
      const k = new Date(c.fecha_cita).toDateString();
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    }
    // Ordenar grupos por fecha asc, citas dentro por hora
    return Array.from(map.entries())
      .map(([k, arr]) => ({ k, fecha: new Date(k), citas: arr.sort((a, b) => new Date(a.fecha_cita) - new Date(b.fecha_cita)) }))
      .sort((a, b) => a.fecha - b.fecha);
  }, [citas]);

  const hoy = new Date();

  if (grupos.length === 0) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed #cbd5e1', background: '#fafbfc' }}>
        <ScheduleIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
        <Typography sx={{ fontSize: 14, color: '#475569', fontWeight: 600 }}>Sin citas en este período</Typography>
        <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Usa "Nueva cita" para agendar</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      {grupos.map((g, i) => {
        const esHoy = mismoDia(g.fecha, hoy);
        return (
          <Box key={g.k}>
            <Box sx={{
              px: 2, py: 1, background: esHoy ? 'linear-gradient(90deg, #eff6ff, #dbeafe)' : '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              borderTop: i > 0 ? '1px solid #e2e8f0' : 'none',
            }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: esHoy ? '#1e3a5f' : '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {DIAS_LARGO[g.fecha.getDay()]} · {g.fecha.getDate()} {MESES[g.fecha.getMonth()]}
                {esHoy && <Chip label="HOY" size="small" sx={{ ml: 1, height: 18, fontSize: 9, fontWeight: 800, background: '#1e3a5f', color: '#fff' }} />}
                <Box component="span" sx={{ ml: 1, color: '#94a3b8', fontWeight: 600 }}>
                  · {g.citas.length} cita{g.citas.length !== 1 ? 's' : ''}
                </Box>
              </Typography>
            </Box>
            {g.citas.map((c, j) => (
              <CitaFilaLista key={c.id} cita={c} esUltimo={j === g.citas.length - 1} onClick={() => onCitaClick(c)} />
            ))}
          </Box>
        );
      })}
    </Paper>
  );
}

function CitaFilaLista({ cita, esUltimo, onClick }) {
  const cfg = getEstadoCfg(cita.estado);
  const tipoCfg = TIPO_CONFIG[cita.tipo_consulta] || TIPO_CONFIG.primera_vez;
  const origenCfg = ORIGEN_CONFIG[cita.origen] || ORIGEN_CONFIG.manual;
  const cancelada = cita.estado === 'cancelado';
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: esUltimo ? 'none' : '1px solid #f1f5f9',
        cursor: 'pointer', opacity: cancelada ? 0.5 : 1,
        '&:hover': { background: '#f8fafc' },
      }}>
      <Box sx={{
        width: 56, textAlign: 'center', flexShrink: 0,
        py: 0.5, borderRadius: 1.5, background: cfg.bg, color: cfg.color,
        borderLeft: `3px solid ${tipoCfg.accent}`,
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1, textDecoration: cancelada ? 'line-through' : 'none' }}>
          {fmtHora(cita.fecha_cita)}
        </Typography>
        <Typography sx={{ fontSize: 9, opacity: 0.7, mt: 0.2 }}>
          {cita.duracion_minutos}m
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.2 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1e293b', textDecoration: cancelada ? 'line-through' : 'none' }}>
            {cita.paciente_nombre}
          </Typography>
          <Tooltip title={origenCfg.label}>
            {React.cloneElement(origenCfg.icon, { sx: { fontSize: 13, color: '#94a3b8' } })}
          </Tooltip>
          <Chip
            icon={cfg.icon}
            label={cfg.label} size="small"
            sx={{
              height: 18, fontSize: 9.5, fontWeight: 700,
              background: cfg.bg, color: cfg.color,
              '& .MuiChip-icon': { color: cfg.color, ml: 0.4 },
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
          {tipoCfg.label}
          {cita.motivo && ` · ${cita.motivo}`}
          {cita.numero_whatsapp && ` · ${cita.numero_whatsapp}`}
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// MINI-CALENDAR (sidebar)
// ============================================================================
function MiniCalendar({ fechaActual, onSelect, citas }) {
  const [mes, setMes] = useState(new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1));
  useEffect(() => { setMes(new Date(fechaActual.getFullYear(), fechaActual.getMonth(), 1)); }, [fechaActual]);

  const dias = useMemo(() => {
    const año = mes.getFullYear();
    const m = mes.getMonth();
    const primer = new Date(año, m, 1);
    const ultimo = new Date(año, m + 1, 0);
    const arr = [];
    const offset = primer.getDay();
    for (let i = offset - 1; i >= 0; i--) {
      const d = new Date(año, m, -i); arr.push({ fecha: d, otroMes: true });
    }
    for (let i = 1; i <= ultimo.getDate(); i++) {
      arr.push({ fecha: new Date(año, m, i), otroMes: false });
    }
    while (arr.length < 42) {
      const last = arr[arr.length - 1].fecha;
      const next = new Date(last); next.setDate(last.getDate() + 1);
      arr.push({ fecha: next, otroMes: true });
    }
    return arr;
  }, [mes]);

  const conteoCitas = useMemo(() => {
    const c = {};
    for (const cita of citas) {
      const k = new Date(cita.fecha_cita).toDateString();
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [citas]);

  const hoy = new Date();

  return (
    <Paper sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e2e8f0', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a5f' }}>
          {MESES[mes.getMonth()]} {mes.getFullYear()}
        </Typography>
        <Box>
          <IconButton size="small" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() - 1, 1))} sx={{ p: 0.3 }}>
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={() => setMes(new Date(mes.getFullYear(), mes.getMonth() + 1, 1))} sx={{ p: 0.3 }}>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.2 }}>
        {DIAS_CORTO.map(d => (
          <Typography key={d} sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textAlign: 'center', py: 0.3 }}>
            {d.charAt(0)}
          </Typography>
        ))}
        {dias.map((dd, i) => {
          const esHoy = mismoDia(dd.fecha, hoy);
          const esSeleccionado = mismoDia(dd.fecha, fechaActual);
          const cuenta = conteoCitas[dd.fecha.toDateString()] || 0;
          return (
            <Box
              key={i}
              onClick={() => onSelect(dd.fecha)}
              sx={{
                aspectRatio: '1', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                borderRadius: 1, cursor: 'pointer',
                fontSize: 11, fontWeight: esSeleccionado || esHoy ? 700 : 500,
                position: 'relative',
                background: esSeleccionado ? '#1e3a5f' : (esHoy ? '#eff6ff' : 'transparent'),
                color: esSeleccionado ? '#fff' : (dd.otroMes ? '#cbd5e1' : (esHoy ? '#1e3a5f' : '#475569')),
                '&:hover': { background: esSeleccionado ? '#1e3a5f' : '#f1f5f9' },
              }}
            >
              {dd.fecha.getDate()}
              {cuenta > 0 && !esSeleccionado && (
                <Box sx={{
                  position: 'absolute', bottom: 2,
                  width: 4, height: 4, borderRadius: '50%',
                  background: '#1e3a5f',
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ============================================================================
// KPIs sidebar
// ============================================================================
function KpiSidebar({ estadisticas }) {
  const items = [
    { label: 'Hoy',         value: estadisticas?.total_hoy || 0,   color: '#0ea5e9', bg: '#e0f2fe' },
    { label: 'Esta semana', value: estadisticas?.total_semana || 0, color: '#1e3a5f', bg: '#eff6ff' },
    { label: 'Confirmadas', value: estadisticas?.confirmadas || 0,  color: '#15803d', bg: '#dcfce7' },
    { label: 'Pendientes',  value: estadisticas?.pendientes || 0,   color: '#b45309', bg: '#fef3c7' },
    { label: 'Canceladas',  value: estadisticas?.canceladas || 0,   color: '#64748b', bg: '#f1f5f9' },
  ];
  return (
    <Paper sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
        Resumen
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {items.map(i => (
          <Box key={i.label} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            py: 0.6, px: 1, borderRadius: 1.5, background: i.bg,
          }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: i.color }}>{i.label}</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: i.color, lineHeight: 1 }}>{i.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
